import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Briefcase,
  Building,
  GraduationCap,
  Pencil,
  Plus,
  ShoppingCart,
  Tags,
  Truck,
  Wifi,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Badge } from '../../components/Badge';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Drawer } from '../../components/Drawer';
import { EmptyState } from '../../components/EmptyState';
import { Input } from '../../components/Input';
import { PageHeader } from '../../components/PageHeader';
import { cn } from '../../lib/cn';
import type {
  PackageCategory,
  PackageCategoryIconKey,
} from '../../mocks/clientBatch';
import { usePackageCategories, addPackageCategory, updatePackageCategory } from '../../api/catalog';

const SLUG_RE = /^[a-z][a-z0-9_-]*$/;

const ICON_KEYS: PackageCategoryIconKey[] = [
  'shopping-cart',
  'graduation-cap',
  'wifi',
  'briefcase',
  'building',
  'truck',
];

const ICON_LABELS: Record<PackageCategoryIconKey, string> = {
  'shopping-cart': 'Cart',
  'graduation-cap': 'School',
  wifi: 'Wifi',
  briefcase: 'Briefcase',
  building: 'Building',
  truck: 'Truck',
};

/** Map a stored iconKey to the corresponding lucide icon component. */
function categoryIcon(key: PackageCategoryIconKey): LucideIcon {
  switch (key) {
    case 'shopping-cart':
      return ShoppingCart;
    case 'graduation-cap':
      return GraduationCap;
    case 'wifi':
      return Wifi;
    case 'briefcase':
      return Briefcase;
    case 'building':
      return Building;
    case 'truck':
      return Truck;
  }
}

function isValidSlug(s: string): boolean {
  return SLUG_RE.test(s);
}

/** Active / Inactive pill (mirrors PricingTiers) */
function CategoryStatusBadge({ active }: { active: boolean }) {
  if (active) return <Badge tone="success">Active</Badge>;
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap bg-zinc-100 text-zinc-500 border-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:border-zinc-700">
      <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 dark:bg-zinc-500" />
      Inactive
    </span>
  );
}

/* ── Page ───────────────────────────────────────────────────────────────── */

type DrawerMode =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; category: PackageCategory };

export function PackageCategories() {
  const { data: initialCategories } = usePackageCategories();
  const queryClient = useQueryClient();
  const [drawer, setDrawer] = useState<DrawerMode>({ kind: 'closed' });

  // Render straight from the query — saves persist via the API and refetch.
  const categories = useMemo(
    () => [...(initialCategories ?? [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    [initialCategories],
  );

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow="Platform"
        title={`Package categories (${categories.length})`}
        subtitle="Organize packages by vertical. Categories where 'Provisions POS' is on trigger Laravel POS database provisioning at signup."
        action={
          <Button
            iconLeft={<Plus className="w-4 h-4" />}
            onClick={() => setDrawer({ kind: 'create' })}
          >
            New category
          </Button>
        }
      />

      <Card className="overflow-hidden">
        {categories.length === 0 ? (
          <EmptyState
            icon={<Tags className="w-5 h-5" />}
            title="No categories yet"
            description="No categories yet — create your first one to start grouping packages."
            action={
              <Button
                iconLeft={<Plus className="w-4 h-4" />}
                onClick={() => setDrawer({ kind: 'create' })}
              >
                New category
              </Button>
            }
          />
        ) : (
          <div className="overflow-x-auto nice-scroll">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50 border-b border-zinc-200 dark:border-zinc-700">
                <tr>
                  <th className="text-left font-medium px-5 sm:px-6 py-3">Slug</th>
                  <th className="text-left font-medium px-4 py-3">Display name</th>
                  <th className="text-left font-medium px-4 py-3 hidden md:table-cell">Icon</th>
                  <th className="text-left font-medium px-4 py-3">Provisions POS</th>
                  <th className="text-right font-medium px-4 py-3 hidden md:table-cell">Packages</th>
                  <th className="text-left font-medium px-4 py-3">Status</th>
                  <th className="px-5 sm:px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
                {categories.map((c) => {
                  const Icon = categoryIcon(c.iconKey);
                  return (
                    <tr
                      key={c.id}
                      className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition"
                    >
                      <td className="px-5 sm:px-6 py-3">
                        <span className="mono text-xs text-zinc-700 dark:text-zinc-200">
                          {c.slug}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-zinc-950 dark:text-zinc-50">
                          {c.displayName}
                        </div>
                        {c.description && (
                          <div className="text-xs text-zinc-500 dark:text-zinc-400 max-w-md">
                            {c.description}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span
                          className="inline-flex items-center gap-1.5 text-zinc-700 dark:text-zinc-200"
                          title={c.iconKey}
                        >
                          <Icon className="w-4 h-4" />
                          <span className="text-xs text-zinc-500 dark:text-zinc-400 mono">
                            {c.iconKey}
                          </span>
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {c.provisionsPos ? (
                          <Badge tone="success">Yes</Badge>
                        ) : (
                          <Badge tone="neutral">No</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-zinc-700 dark:text-zinc-200 hidden md:table-cell">
                        {c.packageCount}
                      </td>
                      <td className="px-4 py-3">
                        <CategoryStatusBadge active={c.isActive} />
                      </td>
                      <td className="px-5 sm:px-6 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => setDrawer({ kind: 'edit', category: c })}
                          aria-label={`Edit ${c.displayName}`}
                          className="w-8 h-8 grid place-items-center rounded-md text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {drawer.kind !== 'closed' && (
        <CategoryDrawer
          mode={drawer}
          onClose={() => setDrawer({ kind: 'closed' })}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['packageCategories'] });
            setDrawer({ kind: 'closed' });
          }}
        />
      )}
    </div>
  );
}

/* ── Drawer ─────────────────────────────────────────────────────────────── */

function CategoryDrawer({
  mode,
  onClose,
  onSaved,
}: {
  mode: { kind: 'create' } | { kind: 'edit'; category: PackageCategory };
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = mode.kind === 'edit';
  const seed = editing ? mode.category : null;

  const [slug, setSlug] = useState<string>(seed?.slug ?? '');
  const [displayName, setDisplayName] = useState(seed?.displayName ?? '');
  const [iconKey, setIconKey] = useState<PackageCategoryIconKey>(
    seed?.iconKey ?? 'shopping-cart',
  );
  const [provisionsPos, setProvisionsPos] = useState<boolean>(
    seed?.provisionsPos ?? false,
  );
  const [isActive, setIsActive] = useState<boolean>(seed?.isActive ?? true);
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const errors = useMemo(() => {
    const e: Partial<{ slug: string; displayName: string }> = {};
    if (!editing && !isValidSlug(slug)) {
      e.slug = 'Lowercase letters, numbers, hyphens, and underscores only (must start with a letter).';
    }
    if (displayName.trim().length === 0) {
      e.displayName = 'Display name is required';
    }
    return e;
  }, [editing, slug, displayName]);

  const valid = Object.keys(errors).length === 0;

  const subscriberPackageCount = seed?.packageCount ?? 0;
  // Warn when toggling provisioning OFF for a category that has subscribers.
  const showProvisioningWarning =
    editing && seed?.provisionsPos === true && !provisionsPos && subscriberPackageCount > 0;

  async function handleSave() {
    if (!valid || submitting) return;
    setSubmitting(true);
    setSaveError(null);
    try {
      if (editing) {
        await updatePackageCategory(seed!.slug, { displayName: displayName.trim(), iconKey, provisionsPos, isActive });
      } else {
        await addPackageCategory({ slug: slug.trim(), displayName: displayName.trim(), iconKey, provisionsPos, isActive });
      }
      onSaved();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not save the category. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Drawer
      open
      onClose={onClose}
      title={editing ? `Edit category · ${seed!.displayName}` : 'New package category'}
      width="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={submitting} disabled={!valid}>
            {editing ? 'Save' : 'Create category'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <Input
          label="Slug"
          required
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          disabled={editing}
          placeholder="pos · school · internet"
          hint={
            editing
              ? 'Slug cannot be changed after creation.'
              : 'Lowercase letters, numbers, hyphens, and underscores. Must start with a letter.'
          }
          error={errors.slug}
          className="mono"
        />

        <Input
          label="Display name"
          required
          placeholder="POS Package · School · Internet"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          error={errors.displayName}
        />

        <fieldset>
          <legend className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
            Icon
          </legend>
          <div className="grid grid-cols-3 gap-2">
            {ICON_KEYS.map((k) => {
              const Icon = categoryIcon(k);
              const active = iconKey === k;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setIconKey(k)}
                  aria-pressed={active}
                  className={cn(
                    'flex flex-col items-center justify-center gap-1.5 px-3 py-3 rounded-md border transition text-xs',
                    active
                      ? 'border-brand text-brand bg-brand-subtle dark:border-brand-dm dark:text-brand-dm dark:bg-brand-dm-subtle/30'
                      : 'border-zinc-200 text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800',
                  )}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{ICON_LABELS[k]}</span>
                </button>
              );
            })}
          </div>
        </fieldset>

        <fieldset className="border-t border-zinc-200 dark:border-zinc-700 pt-5">
          <legend className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
            Provisioning
          </legend>
          <label className="flex items-start gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
            <input
              type="checkbox"
              checked={provisionsPos}
              onChange={(e) => setProvisionsPos(e.target.checked)}
              className="mt-0.5 rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
            />
            <span>
              <strong>Provisions POS</strong> — when ON, signups under this category
              trigger Laravel POS database provisioning at <span className="mono">/signup</span>.
              When OFF, the category is billing-only.
            </span>
          </label>
        </fieldset>

        <fieldset className="border-t border-zinc-200 dark:border-zinc-700 pt-5">
          <legend className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
            Availability
          </legend>
          <label className="flex items-start gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="mt-0.5 rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
            />
            <span>
              <strong>Active</strong> — resellers can pick this category when creating a
              package.
            </span>
          </label>
        </fieldset>

        {showProvisioningWarning && (
          <Banner tone="warning" title="Existing businesses unaffected">
            {subscriberPackageCount}{' '}
            {subscriberPackageCount === 1 ? 'business is' : 'businesses are'} currently
            provisioned under this category. Turning off provisioning won&apos;t affect
            them — only NEW signups.
          </Banner>
        )}

        {saveError && (
          <Banner tone="error" title="Couldn’t save the category">
            {saveError}
          </Banner>
        )}
      </div>
    </Drawer>
  );
}
